import { CommonProps } from "@/components/forms/types";

export enum NumberValidationRule {
    Decimals = "decimals",
    Max = "maxvalue",
    Min = "minvalue",
    Number = "knumber",
}

export type NumberValidationRules = CommonProps<
    [NumberValidationRule.Decimals | NumberValidationRule.Max | NumberValidationRule.Min, number]
>["validation"];

/**
 * Hämta min eller max, baserat på type, från validation
 */
export const getNumberConstraint = ({
    type,
    validation,
}: {
    type: "min" | "max";
    validation?: NumberValidationRules;
}) => {
    const validationRegex = type === "min" ? new RegExp(/minvalue:\d+/) : new RegExp(/maxvalue:\d+/);

    if (!validation) {
        return null;
    }

    if (Array.isArray(validation)) {
        const validationValue = validation.find(([rule]) =>
            type === "min" ? rule === NumberValidationRule.Min : rule === NumberValidationRule.Max
        )?.[1];

        if (isNaN(Number(validationValue))) {
            return null;
        }

        return Number(validationValue);
    } else {
        const validationValue = (validation as string).match(validationRegex)?.[0]?.split?.(":")?.[1];

        if (isNaN(Number(validationValue))) {
            return null;
        }

        return Number(validationValue);
    }
};

export const getMaxDecimals = ({ validation }: { validation?: CommonProps["validation"] }) => {
    if (!validation) {
        return null;
    }

    if (Array.isArray(validation)) {
        return validation.find(([rule]) => rule === NumberValidationRule.Decimals)?.[1] ?? 0;
    } else {
        return Number((validation as string).match(/decimals:\d+/)?.[0]?.split?.(":")?.[1] ?? 0);
    }
};

export const validateDecimals = ({ value, max }: { value: string; max: number }) => {
    const decimalCheck = new RegExp(/,\d+/);

    if (!decimalCheck.test(value)) {
        return true;
    }

    const decimals = value.match(/,\d+/)?.[0]?.split?.(",")?.[1] ?? "";

    return decimals.length <= (max ?? 0);
};

export const validateNumber = ({ value }: { value?: string }) => {
    const number = Number(value?.replace?.(",", ".") ?? value);

    return !isNaN(number);
};

/**
 * Validera att värdet är >= min eller <= max, beroende på inskickad type
 */
export const validateMinMax = ({
    value,
    type,
    constraint,
}: {
    value?: string;
    type: "min" | "max";
    constraint?: number;
}) => {
    const number = Number(value?.replace?.(",", ".") ?? value);

    // Vi bryr oss inte om att visa ett felmeddelande på minmax om värdet som skickats in inte är ett nummer
    if (isNaN(number) || value === "") {
        return true;
    }

    if (type === "min" && typeof constraint === "number") {
        return number >= constraint;
    } else if (type === "max" && typeof constraint === "number") {
        return number <= constraint;
    }

    return true;
};

/**
 * Skickar tillbaka array/sträng som inkluderar våra customregler för nummer, utöver det som redan fanns i valideringen.
 * Den kollar att reglerna redan inte existerar.
 * Strukturen på validation är antingen en sträng med | som separator (t.ex required|number) eller
 * en array med arrayer, där första item i nestade arrayerna är namn på regeln
 * (t.ex [["minvalue", 0], ["decimals": 0], ["required, true]]). Se dokumentation för Formulate/Formkit
 *
 * NOTE! Regler i baseValidation overridar inskickade max/min/decimals (om de redan är satta)
 */
export const createNumberValidation = ({
    baseValidation,
    max,
    min,
    decimals = 0,
}: {
    baseValidation: CommonProps["validation"];
    max?: number;
    min?: number;
    decimals?: number;
}) => {
    if (typeof baseValidation === "string") {
        let modified = `${baseValidation}`;

        if (!modified.match(/(^knumber|\|knumber\||\|knumber$)/g)) {
            modified = `${modified}|${NumberValidationRule.Number}`;
        }

        // Lägger till check av att antal decimaler är 0, om inte det skickats med i validation
        if (!modified.match(/(^decimals:\d|\|decimals:\d\||\|decimals:\d$)/g)) {
            modified = `${modified}|decimals:${decimals}`;
        }

        if (!modified.match(/(^maxvalue:\d+|\|maxvalue:\d+\||\|maxvalue:\d+$)/g) && typeof max === "number") {
            modified = `${modified}|maxvalue:${max}`;
        }

        if (!modified.match(/(^minvalue:\d+|\|minvalue:\d+\||\|minvalue:\d+$)/g) && typeof min === "number") {
            modified = `${modified}|minvalue:${min}`;
        }

        return modified;
    } else if (baseValidation && Array.isArray(baseValidation)) {
        const modified = [...baseValidation];

        if (!modified.find(([rule]) => rule === NumberValidationRule.Number)) {
            modified.push([NumberValidationRule.Number]);
        }

        if (!modified.find(([rule]) => rule === NumberValidationRule.Decimals)) {
            modified.push([NumberValidationRule.Decimals, 0]);
        }

        if (typeof min === "number" && !modified.find(([rule]) => rule === NumberValidationRule.Min)) {
            modified.push([NumberValidationRule.Min, min]);
        }

        if (typeof max === "number" && !modified.find(([rule]) => rule === NumberValidationRule.Max)) {
            modified.push([NumberValidationRule.Max, max]);
        }

        return modified;
    }

    return baseValidation;
};
